import {lazy, useMemo} from "react";
import {RoutePaths} from "../../../util/constants";
import DefaultLayout from "../../../layout/default/DefaultLayout";
import {RootState, useAppSelector} from "../../../store";
import PanelLayout from "../../../layout/panel/PanelLayout";
import AuthLayout from "../../../layout/auth/AuthLayout";
import {useTranslation} from "react-i18next";
import Home from "../../home/Home";
import EmptyLayout from "../../../layout/empty/EmptyLayout";

const Wishes = lazy(() => import('../../wishes/Wishes'))
const HowItWorks = lazy(() => import('../../how-it-works/HowItWorks'))
const Auth = lazy(() => import('../../auth/Auth'))

const ForgotPassword = lazy(() => import('../../auth/forgot-password/ForgotPassword'))
const ResetPassword = lazy(() => import('../../auth/reset-password/ResetPassword'))
const EmailConfirmation = lazy(() => import('../../auth/email-confirmation/EmailConfirmation'))

const WishDetails = lazy(() => import('../../wish-details/WishDetails'))
const Winner = lazy(() => import('../../winner/Winner'))
const About = lazy(() => import('../../about/About'))
const Profile = lazy(() => import('../../profile/Profile'))
const UnsubscribeNewsletter = lazy(() => import('../../unsubscribe-newsletter/UnsubscribeNewsletter'))

const AdminPanelLogin = lazy(() => import('../../admin-panel/login/AdminPanelLogin'))
const AdminPanelDashboard = lazy(() => import('../../admin-panel/dashboard/AdminPanelDashboard'))
const AdminPanelWishes = lazy(() => import('../../admin-panel/wishes/AdminPanelWishes'))
const AdminPanelCategories = lazy(() => import('../../admin-panel/categories/AdminPanelCategories'))
const AdminPanelQuestions = lazy(() => import('../../admin-panel/questions/AdminPanelQuestions'))
const AdminPanelClients = lazy(() => import('../../admin-panel/clients/AdminPanelClients'))
const AdminPanelContacts = lazy(() => import('../../admin-panel/messages/AdminPanelMessages'))
const AdminPanelExpenses = lazy(() => import('../../admin-panel/expenses/AdminPanelExpenses'))
const AdminPanelBlogs = lazy(() => import('../../admin-panel/blogs/AdminPanelBlogs'))
const AdminPanelNotifications = lazy(() => import('../../admin-panel/notifications/AdminPanelNotifications'))
const AdminPanelDocuments = lazy(() => import('../../admin-panel/documents/AdminPanelDocuments'))
const AdminPanelImages = lazy(() => import('../../admin-panel/images/AdminPanelImages'))
const AdminPanelConfig = lazy(() => import('../../admin-panel/config/AdminPanelConfig'))
const AdminPanelEmails = lazy(() => import('../../admin-panel/emails/AdminPanelEmails'))

const ClientPanelDashboard = lazy(() => import('../../client-panel/dashboard/ClientPanelDashboard'))
const ClientPanelVouchers = lazy(() => import('../../client-panel/vouchers/ClientPanelVouchers'))
const ClientPanelDraws = lazy(() => import('../../client-panel/draws/ClientPanelDraws'))

const Blogs = lazy(() => import('../../blogs/Blogs'))
const BlogDetails = lazy(() => import('../../blogs/blog-details/BlogDetails'))

const Contact = lazy(() => import('../../contact/Contact'))
const Guide = lazy(() => import('../../guide/Guide'))
const Faq = lazy(() => import('../../faq/Faq'))

const Terms = lazy(() => import('../../terms/Terms'))
const Privacy = lazy(() => import('../../privacy/Privacy'))

const useAppRoutes = () => {
    const token = useAppSelector((state: RootState) => state.authReducer.loggedInUser.access_token)
    const isAdmin = useAppSelector((state: RootState) => state.authReducer.loggedInUser.is_admin)
    const isClient = useAppSelector((state: RootState) => !!state.authReducer.loggedInUser.client_id)
    const clientContactEmail = useAppSelector((state: RootState) => state.clientReducer.selectedClient.contact)
    const currentClientEmail = useAppSelector((state: RootState) => state.authReducer.loggedInUser.email)
    const comingSoon = useAppSelector((s: RootState) => s.appReducer.featureToggles.coming_soon);
    const {t} = useTranslation()

    const routes = useMemo(() => (
        [
            {
                path: RoutePaths.HOME,
                component: Home,
                layout: DefaultLayout,
                showRoute: true,
                title: t('home')
            },
            {
                path: RoutePaths.WISHES,
                component: Wishes,
                layout: DefaultLayout,
                showRoute: true,
                title: t('wishes')
            },
            {
                path: RoutePaths.WISH_DETAILS,
                component: WishDetails,
                layout: DefaultLayout,
                showRoute: true,
            },
            {
                path: RoutePaths.ABOUT,
                component: About,
                layout: DefaultLayout,
                showRoute: true,
                title: t('about')
            },
            {
                path: RoutePaths.HOW_IT_WORKS,
                component: HowItWorks,
                layout: DefaultLayout,
                showRoute: true,
                title: t('how_it_works')
            },
            {
                path: RoutePaths.AUTH,
                component: Auth,
                layout: AuthLayout,
                showRoute: !token.length,
                redirectTo: RoutePaths.HOME,
                title: t('login_register')
            },
            {
                path: RoutePaths.PROFILE,
                component: Profile,
                layout: DefaultLayout,
                showRoute: !!token.length && !isAdmin && !isClient,
                redirectTo: RoutePaths.HOME,
                title: t('your_profile')
            },
            {
                path: RoutePaths.FORGOT_PASSWORD,
                component: ForgotPassword,
                layout: AuthLayout,
                showRoute: !token.length,
                redirectTo: RoutePaths.HOME,
                title: t('forgot_password')
            },
            {
                path: RoutePaths.RESET_PASSWORD,
                component: ResetPassword,
                layout: AuthLayout,
                showRoute: !token.length,
                redirectTo: RoutePaths.HOME,
                title: t('reset_password')
            },
            {
                path: RoutePaths.EMAIL_CONFIRMATION,
                component: EmailConfirmation,
                layout: AuthLayout,
                showRoute: !token.length,
                redirectTo: RoutePaths.HOME,
                title: t('e-mail')
            },
            {
                path: RoutePaths.TERMS,
                component: Terms,
                layout: DefaultLayout,
                showRoute: true,
                title: t('terms')
            },
            {
                path: RoutePaths.PRIVACY,
                component: Privacy,
                layout: DefaultLayout,
                showRoute: true,
                title: t('privacy')
            },
            {
                path: RoutePaths.ADMIN_PANEL_DASHBOARD,
                component: AdminPanelDashboard,
                layout: PanelLayout,
                showRoute: !!token.length && isAdmin,
                redirectTo: RoutePaths.HOME,
                title: 'AP Dashboard'
            },
            {
                path: RoutePaths.ADMIN_PANEL_WISHES,
                component: AdminPanelWishes,
                layout: PanelLayout,
                showRoute: !!token.length && isAdmin,
                redirectTo: RoutePaths.HOME,
                title: 'AP Wishes'
            },
            {
                path: RoutePaths.ADMIN_PANEL_LOGIN,
                component: AdminPanelLogin,
                layout: EmptyLayout,
                showRoute: !token.length && comingSoon,
                redirectTo: isAdmin ? RoutePaths.ADMIN_PANEL_DASHBOARD : RoutePaths.HOME,
                title: 'AP Login'
            },
            {
                path: RoutePaths.ADMIN_PANEL_CATEGORIES,
                component: AdminPanelCategories,
                layout: PanelLayout,
                showRoute: !!token.length && isAdmin,
                redirectTo: RoutePaths.HOME,
                title: 'AP Categories'
            },
            {
                path: RoutePaths.ADMIN_PANEL_QUESTIONS,
                component: AdminPanelQuestions,
                layout: PanelLayout,
                showRoute: !!token.length && isAdmin,
                redirectTo: RoutePaths.HOME,
                title: 'AP Questions'
            },
            {
                path: RoutePaths.ADMIN_PANEL_CLIENTS,
                component: AdminPanelClients,
                layout: PanelLayout,
                showRoute: !!token.length && isAdmin,
                redirectTo: RoutePaths.HOME,
                title: 'AP Clients'
            },
            {
                path: RoutePaths.ADMIN_PANEL_MESSAGES,
                component: AdminPanelContacts,
                layout: PanelLayout,
                showRoute: !!token.length && isAdmin,
                redirectTo: RoutePaths.HOME,
                title: 'AP Messages'
            },
            {
                path: RoutePaths.ADMIN_PANEL_EXPENSES,
                component: AdminPanelExpenses,
                layout: PanelLayout,
                showRoute: !!token.length && isAdmin,
                redirectTo: RoutePaths.HOME,
                title: 'AP Expenses'
            },
            {
                path: RoutePaths.ADMIN_PANEL_BLOGS,
                component: AdminPanelBlogs,
                layout: PanelLayout,
                showRoute: !!token.length && isAdmin,
                redirectTo: RoutePaths.HOME,
                title: 'AP Blogs'
            },
            {
                path: RoutePaths.ADMIN_PANEL_NOTIFICATIONS,
                component: AdminPanelNotifications,
                layout: PanelLayout,
                showRoute: !!token.length && isAdmin,
                redirectTo: RoutePaths.HOME,
                title: 'AP Notifications'
            },
            {
                path: RoutePaths.ADMIN_PANEL_DOCUMENTS,
                component: AdminPanelDocuments,
                layout: PanelLayout,
                showRoute: !!token.length && isAdmin,
                redirectTo: RoutePaths.HOME,
                title: 'AP Documents'
            },
            {
                path: RoutePaths.ADMIN_PANEL_IMAGES,
                component: AdminPanelImages,
                layout: PanelLayout,
                showRoute: !!token.length && isAdmin,
                redirectTo: RoutePaths.HOME,
                title: 'AP Images'
            },
            {
                path: RoutePaths.ADMIN_PANEL_CONFIG,
                component: AdminPanelConfig,
                layout: PanelLayout,
                showRoute: !!token.length && isAdmin,
                redirectTo: RoutePaths.HOME,
                title: 'AP Config'
            },
            {
                path: RoutePaths.ADMIN_PANEL_EMAILS,
                component: AdminPanelEmails,
                layout: PanelLayout,
                showRoute: !!token.length && isAdmin,
                redirectTo: RoutePaths.HOME,
                title: 'AP Emails'
            },
            {
                path: RoutePaths.CONTACT,
                component: Contact,
                layout: DefaultLayout,
                showRoute: true,
                title: t('contact')
            },
            {
                path: RoutePaths.BLOGS,
                component: Blogs,
                layout: DefaultLayout,
                showRoute: true,
                title: t('blogs')
            },
            {
                path: RoutePaths.BLOG_DETAILS,
                component: BlogDetails,
                layout: DefaultLayout,
                showRoute: true,
            },
            {
                path: RoutePaths.FAQ,
                component: Faq,
                layout: DefaultLayout,
                showRoute: true,
                title: t('faq')
            },
            {
                path: RoutePaths.GUIDE,
                component: Guide,
                layout: DefaultLayout,
                showRoute: true,
                title: t('guide')
            },
            {
                path: RoutePaths.UNSUBSCRIBE_NEWSLETTER,
                component: UnsubscribeNewsletter,
                layout: DefaultLayout,
                showRoute: true,
                title: t('unsubscribe_success_title')
            },
            {
                path: RoutePaths.WISH_WINNER,
                component: Winner,
                layout: DefaultLayout,
                showRoute: !!token.length && !isAdmin && !isClient,
                redirectTo: RoutePaths.HOME,
                title: t('congratulations')
            },
            {
                path: RoutePaths.CLIENT_PANEL_DASHBOARD,
                component: ClientPanelDashboard,
                layout: PanelLayout,
                showRoute: !!token.length && isClient && clientContactEmail === currentClientEmail,
                redirectTo: RoutePaths.HOME,
                title: 'CP Dashboard'
            },
            {
                path: RoutePaths.CLIENT_PANEL_VOUCHERS,
                component: ClientPanelVouchers,
                layout: PanelLayout,
                showRoute: !!token.length && isClient,
                redirectTo: RoutePaths.HOME,
                title: 'CP Vouchers'
            },
            {
                path: RoutePaths.CLIENT_PANEL_DRAWS,
                component: ClientPanelDraws,
                layout: PanelLayout,
                showRoute: !!token.length && isClient,
                redirectTo: RoutePaths.HOME,
                title: 'CP Draws'
            },
        ]
    ), [token, isAdmin, isClient, t, clientContactEmail, currentClientEmail, comingSoon])

    return [routes]
}

export default useAppRoutes;
